import React from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { usePortalQuery, usePortalMutation } from '../../hooks/usePortalQuery';
import { createTask, deleteTask, listTasks, updateTask } from '../../lib/portalApi';
import { can } from '../../lib/portalPermissions';
import { Button } from '../ui/Button';
import { inputClass } from '../ui/Field';
import { EmptyState, InlineError, QueryState } from './QueryState';
import { daysUntil, displayName, formatDate, TASK_STATUS_LABELS } from '../../utils/portalFormat';
import type { OrgRole, PortalUser, TaskStatus } from '../../types/portal';
const COLUMNS: TaskStatus[] = ['todo', 'in_progress', 'review', 'done'];
interface TaskBoardProps {
  orgId: string;
  projectId: string;
  role: OrgRole;
  people: PortalUser[];
}

/**
 * Tasks grouped by status. Members and above change status and assignee inline;
 * viewers see the same board read only. Only admins and owners can delete.
 */
export function TaskBoard({
  orgId,
  projectId,
  role,
  people
}: TaskBoardProps) {
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => listTasks(orgId, projectId), [orgId, projectId]);
  const editable = can(role, 'tasks.update');
  const update = usePortalMutation(updateTask);
  const remove = usePortalMutation(deleteTask);
  const [adding, setAdding] = React.useState(false);
  const nameFor = (id: string | null) => {
    const person = people.find(p => p.id === id);
    return person ? displayName(person) : 'Unassigned';
  };
  return <div data-id="element-1055" data-mp="d8x5bc0epw4u">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1056" data-mp="i9l4ikyshja5">
        <p className="text-[14px] text-muted" data-id="element-1057" data-mp="i7qjl6nipr62">{editable ? 'Change a status or owner directly from the list.' : 'Read only. Your role does not include editing tasks.'}</p>
        {can(role, 'tasks.create') && !adding && <Button size="sm" variant="secondary" onClick={() => setAdding(true)} data-id="element-1058" data-mp="3qpwddlcnwotu">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1059" data-mp="3qntoj4n8x1qp" />
            Add task
          </Button>}
      </div>

      {adding && <NewTaskForm orgId={orgId} projectId={projectId} people={people} onDone={() => setAdding(false)} data-id="element-1060" data-mp="3nnjllxqby1i2" />}
      <div className="mt-3 space-y-2" data-id="element-1061" data-mp="3ipqdgz5guxf5">
        <InlineError error={update.error ?? remove.error} data-id="element-1062" data-mp="3ioorw0qtqsj6" />
      </div>

      <div className="mt-5" data-id="element-1063" data-mp="175iebmyqa28">
        <QueryState loading={loading} error={error} data-id="element-1064" data-mp="1ajljm3ganaq">
          {data && data.length === 0 ? <EmptyState title="No tasks yet" body="Tasks agreed with the team will appear here." data-id="element-1065" data-mp="3sfjlxrabawf6" /> : <div className="space-y-8" data-id="element-1066" data-mp="34r0fzieqivq9">
              {COLUMNS.map(column => {
            const tasks = (data ?? []).filter(t => t.status === column);
            if (!tasks.length) return null;
            return <section key={column} aria-labelledby={`col-${column}`} data-id="element-1067" data-mp="39o0mtvjes2if">
                    <h3 id={`col-${column}`} className="flex items-center gap-2 text-[13px] font-medium uppercase tracking-[0.12em] text-muted" data-id="element-1068" data-mp="tx4f8t7efbhg">
                      {TASK_STATUS_LABELS[column]}
                      <span className="rounded-full bg-panel px-2 py-0.5 text-[12px] normal-case tracking-normal text-body" data-id="element-1069" data-mp="whk8em98h52n">{tasks.length}</span>
                    </h3>
                    <ul className="mt-3 divide-y divide-line rounded-md border border-line" data-id="element-1070" data-mp="11hyie9ueap3b">
                      {tasks.map(task => {
                  const days = task.dueDate ? daysUntil(task.dueDate) : null;
                  const late = days !== null && days < 0 && task.status !== 'done';
                  return <li key={task.id} className="grid gap-3 px-4 py-4 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-6" data-id="element-1071" data-mp="1k7o9omyy8sqh">
                            <div className="min-w-0" data-id="element-1072" data-mp="16eyoulv5ptuy">
                              <p className={`text-[15px] ${task.status === 'done' ? 'text-muted line-through decoration-line' : 'text-ink'}`} data-id="element-1073" data-mp="1ah3fdbqb6add">{task.title}</p>
                              {task.description && <p className="mt-1 text-[13px] text-muted" data-id="element-1074" data-mp="1y533s505doos">{task.description}</p>}
                              <p className={`mt-1 text-[13px] ${late ? 'text-[#FFA192]' : 'text-muted'}`} data-id="element-1075" data-mp="22zqsa3v5ij3d">
                                {task.dueDate ? `Due ${formatDate(task.dueDate)}${late ? ', late' : ''}` : 'No due date'}
                                {!editable && `, ${nameFor(task.assignedTo)}`}
                              </p>
                            </div>
                            {editable && <div className="flex flex-wrap items-center gap-2" data-id="element-1076" data-mp="xzst2n7ooorm">
                                <label className="sr-only" htmlFor={`status-${task.id}`} data-id="element-1077" data-mp="y1n7rjsd3kn9">
                                  Status of {task.title}
                                </label>
                                <select id={`status-${task.id}`} value={task.status} onChange={e => void update.run(orgId, projectId, task.id, {
                        status: e.target.value as TaskStatus
                      })} className="h-9 rounded-sm border border-line bg-charcoal px-2 text-[13px] text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-1078" data-mp="o7wkujbvvi9o">
                                  {COLUMNS.map(s => <option key={s} value={s} data-id="element-1079" data-mp="33lyn0mexgn8j">
                                      {TASK_STATUS_LABELS[s]}
                                    </option>)}
                                </select>
                                <label className="sr-only" htmlFor={`owner-${task.id}`} data-id="element-1080" data-mp="3djd3un4rje1d">
                                  Owner of {task.title}
                                </label>
                                <select id={`owner-${task.id}`} value={task.assignedTo ?? ''} onChange={e => void update.run(orgId, projectId, task.id, {
                        assignedTo: e.target.value || null
                      })} className="h-9 max-w-[180px] rounded-sm border border-line bg-charcoal px-2 text-[13px] text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-1081" data-mp="3nd3qrnl8rgey">
                                  <option value="" data-id="element-1082" data-mp="2ayc2peuw3hr2">Unassigned</option>
                                  {people.map(p => <option key={p.id} value={p.id} data-id="element-1083" data-mp="267c13watsbi8">
                                      {displayName(p)}
                                    </option>)}
                                </select>
                                {can(role, 'tasks.delete') && <button type="button" onClick={() => window.confirm(`Delete “${task.title}”?`) && void remove.run(orgId, projectId, task.id)} className="inline-flex h-9 w-9 items-center justify-center rounded-sm text-muted transition-colors duration-150 hover:bg-panel hover:text-[#FFA192]" aria-label={`Delete ${task.title}`} data-id="element-1084" data-mp="2tw5061mquzh2">
                                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-1085" data-mp="3b5qsxjrbwlao" />
                                  </button>}
                              </div>}
                          </li>;
                })}
                    </ul>
                  </section>;
          })}
            </div>}
        </QueryState>
      </div>
    </div>;
}
function NewTaskForm({
  orgId,
  projectId,
  people,
  onDone
}: {
  orgId: string;
  projectId: string;
  people: PortalUser[];
  onDone: () => void;
}) {
  const [title, setTitle] = React.useState('');
  const [assignedTo, setAssignedTo] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const create = usePortalMutation(createTask);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const task = await create.run(orgId, projectId, {
      title,
      assignedTo: assignedTo || null,
      dueDate: dueDate || null
    });
    if (task) onDone();
  };
  return <form onSubmit={submit} className="mt-5 rounded-md border border-line bg-panel p-5" data-id="element-1086" data-mp="2qw64pz5fi8ti">
      <div className="grid gap-4 sm:grid-cols-[1fr_200px_170px]" data-id="element-1087" data-mp="2lxw8dl7yx26b">
        <div data-id="element-1088" data-mp="2lvig1k4oa42g">
          <label htmlFor="new-task-title" className="block text-[13px] font-medium text-ink" data-id="element-1089" data-mp="2gyjp1agb2d8h">
            Task
          </label>
          <input id="new-task-title" autoFocus value={title} onChange={e => setTitle(e.target.value)} className={`${inputClass} mt-1.5`} maxLength={500} data-id="element-1090" data-mp="2cwopaae0w7wt" />
        </div>
        <div data-id="element-1091" data-mp="22yqq4rumkqpf">
          <label htmlFor="new-task-owner" className="block text-[13px] font-medium text-ink" data-id="element-1092" data-mp="22z0hojvbj5x8">
            Owner
          </label>
          <select id="new-task-owner" value={assignedTo} onChange={e => setAssignedTo(e.target.value)} className={`${inputClass} mt-1.5`} data-id="element-1093" data-mp="65fe1ffvxwi1">
            <option value="" data-id="element-1094" data-mp="15kpvjnhvl0o">Unassigned</option>
            {people.map(p => <option key={p.id} value={p.id} data-id="element-1095" data-mp="8qmxc3sjsvm4">
                {displayName(p)}
              </option>)}
          </select>
        </div>
        <div data-id="element-1096" data-mp="ig6hdcrzyvik">
          <label htmlFor="new-task-due" className="block text-[13px] font-medium text-ink" data-id="element-1097" data-mp="ihraqz2ks8eu">
            Due
          </label>
          <input id="new-task-due" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className={`${inputClass} mt-1.5`} data-id="element-1098" data-mp="nf9od2rjfbt6" />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-1099" data-mp="3qvq7w41a9ztx">
        <Button type="submit" size="sm" disabled={create.pending} data-id="element-1100" data-mp="3vvm95viljd5s">
          {create.pending ? 'Adding' : 'Add task'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone} data-id="element-1101" data-mp="4pl3y3lbbds2">
          Cancel
        </Button>
        <InlineError error={create.error} data-id="element-1102" data-mp="1b4c4252m2ul1" />
      </div>
    </form>;
}